<template>
    <button class="wp-element-button wc-block-components-product-button__button add_to_cart_button" @click="openModal">
        {{ openDesignerButtonText }}
    </button>

    <Modal :open="isOpen" :title="designerTitle" @close="closeModal">
        <form-wizard
            ref="wizard"
            @on-complete="onComplete"
            shape="tab"
            color="#111111"
            :nextButtonText="nextButtonText"
            :backButtonText="backButtonText"
            :finishButtonText="finishButtonText"
        >
            <tab-content class="tab-content" :title="sourceViewTitle" :before-change="showEditor">
                <Map v-if="sourceMode === 'map'" ref="mapview" />
                <ImageUploadControl v-if="sourceMode === 'photo'" ref="imageUpload" />
            </tab-content>

            <tab-content :title="editViewTitle" :before-change="showPreview">
                <EditControl ref="editControl" />
            </tab-content>

            <tab-content :title="previewViewTitle">
                <PreviewControl ref="previewControl" />
            </tab-content>

        </form-wizard>
    </Modal>
</template>

<script>
import { inject, ref } from "vue";
import Modal from "./components/Modal.vue";
import SliderControl from "./components/SliderControl.vue";
import Map from "./components/Map.vue";
import ImageUploadControl from "./components/ImageUploadControl.vue";
import EditControl from "./components/EditControl.vue";
import PreviewControl from "./components/PreviewControl.vue";
import CheckboxControl from "./components/CheckboxControl.vue";
import ColorSelectControl from "./components/ColorSelectControl.vue";
import { FormWizard, TabContent } from "vue3-form-wizard";
import "vue3-form-wizard/dist/style.css";

export default {
    name: "App",
    components: {
        Map,
        ImageUploadControl,
        Modal,
        SliderControl,
        CheckboxControl,
        ColorSelectControl,
        FormWizard,
        TabContent,
        EditControl,
        PreviewControl,
    },
    setup() {
        // Inject dependencies
        const config = inject("config");

        // Get designer variant
        const designerVariant = config.product_variants.find(
            (variant) => Number(variant.id) === Number(config.product.productVariantId)
        );

        if (!designerVariant) {
            console.error("Designer variant not found");
        }

        const designerTitle = config.texts.designerTitle;
        const openDesignerButtonText = config.texts.openDesignerButtonText;
        const nextButtonText = config.texts.nextButtonText;
        const backButtonText = config.texts.backButtonText;
        const finishButtonText = config.texts.finishButtonText;

        const editViewTitle = config.texts.editView.viewTitle;
        const previewViewTitle = config.texts.previewView.viewTitle;

        // The designer mode is fixed per product (set in WordPress) — there is no
        // in-app switch between map and photo upload for a given product.
        const sourceMode = config.product.designerMode === "image_upload" ? "photo" : "map";
        const sourceViewTitle =
            sourceMode === "photo" ? config.texts.imageUploadView.viewTitle : config.texts.mapView.viewTitle;

        return {
            config,
            designerVariant,
            designerTitle,
            openDesignerButtonText,
            nextButtonText,
            backButtonText,
            finishButtonText,
            editViewTitle,
            previewViewTitle,
            sourceMode,
            sourceViewTitle,
        };
    },
    data() {
        return {
            isOpen: false,
        };
    },
    computed: {
        activeSourceControl() {
            return this.sourceMode === "photo" ? this.$refs.imageUpload : this.$refs.mapview;
        },
    },

    methods: {
        openModal() {
            this.$refs.wizard.reset();
            this.isOpen = true;
        },

        closeModal() {
            this.isOpen = false;
        },

        async showEditor() {
            try {
                await this.renderImage();
            } catch (err) {
                console.error("Source image render failed", err);
                const errorTexts = this.sourceMode === "photo" ? this.config.texts.imageUploadView : this.config.texts.mapView;
                window.alert(errorTexts.renderErrorText || "Das Bild konnte nicht verarbeitet werden. Bitte versuchen Sie es erneut.");
                // Returning false keeps the wizard on the current step.
                return false;
            }
            return true;
        },

        async showPreview() {
            // Dithering/quantisation runs asynchronously in a worker after any edit
            // (initial render, slider change, color change). If it hasn't finished
            // yet, wait for it so we never submit the un-quantised RGB preview image.
            const editControl = this.$refs.editControl;
            if (editControl.isProcessing) {
                await new Promise((resolve) => {
                    const stopWatch = this.$watch(
                        () => editControl.isProcessing,
                        (isProcessing) => {
                            if (!isProcessing) {
                                stopWatch();
                                resolve();
                            }
                        }
                    );
                });
            }

            const img = editControl.getDistortedImageBase64();
            const isPortrait = this.activeSourceControl.isPortrait;
            const colorPalette = editControl.getColorPalette();
            this.$refs.previewControl.initControl(img, colorPalette, isPortrait);

            return true;
        },

        // Callbacks
        onComplete() {
            this.$refs.previewControl.addToCart();
        },

        async renderImage() {
            // Use a Promise to handle the async operation
            await new Promise((resolve, reject) => {
                // Callback function to get the current source image (map crop or uploaded photo crop)
                const isPortrait = this.activeSourceControl.isPortrait;
                this.activeSourceControl.getCurrentImage(async (imageData) => {
                    if (imageData) {
                        // Directly set the image source here
                        await this.$refs.editControl.initControl(imageData, isPortrait);
                        resolve();
                    } else {
                        reject("Image rendering failed");
                    }
                });
            });

            return true;
        },
    },
};
</script>

<style>
</style>


